import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * JSON-LD helper: Organization schema for ProposAI.
 */
export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'ProposAI',
  url: 'https://proposai.ca',
  logo: 'https://proposai.ca/og-image.png',
  description: 'AI-powered RFP management platform for Canadian businesses. Bilingual EN/FR, PIPEDA-compliant.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CA',
  },
  sameAs: ['https://proposai.ca'],
});

/**
 * JSON-LD helper: SoftwareApplication schema for ProposAI.
 */
export const buildSoftwareApplicationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'ProposAI',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: 'https://proposai.ca',
  description: 'AI-powered RFP creation, vendor matching, and procurement management for Canadian businesses.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'CAD',
    description: 'Free plan available. Pay-as-you-go at $10/RFP or Consultant plan at $49.99/month.',
  },
  featureList: [
    'AI RFP Generation',
    'Vendor Marketplace',
    'AI Vendor Analysis',
    'Bilingual EN/FR',
    'PIPEDA Compliance',
  ],
});

/**
 * JSON-LD helper: FAQPage schema from an array of {q, a} items.
 */
export const buildFaqPageJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

const SEO = ({
  title,
  description,
  name,
  type,
  url,
  image,
  jsonLdData
}) => {
  const siteUrl = url || "https://proposai.ca";
  const siteImage = image || "https://proposai.ca/og-image.png";

  return (
    <Helmet>
      { /* Standard metadata tags */ }
      <title>{title}</title>
      <meta name="description" content={description} />

      { /* OpenGraph tags */ }
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:image" content={siteImage} />

      { /* Twitter tags */ }
      <meta name="twitter:creator" content={name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={siteImage} />

      { /* JSON-LD Structured Data for AEO/GEO */ }
      {jsonLdData && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
